import { useCallback, useEffect, useRef, useState } from 'react';
import { BellRing, CheckCircle2, ChevronDown, FileQuestion, FileUp, Link2, Loader2, RefreshCw, Save, Search, Send, Trash2, Upload, Users, XCircle } from 'lucide-react';
import { Checkbox, SearchSelect } from './FieldComponents';
import Modal from './Modal';
import CardholderMappingsModal from './CardholderMappingsModal';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import '../styles/AccountingPortal.css';

const RECEIPT_API = 'https://api.casedb.co/expense-receipts.php';

const filterOptions = [
    ['pending', 'Pending'],
    ['resolved', 'Resolved'],
    ['unmatched', 'Unmatched'],
    ['deleted', 'Deleted'],
    ['all', 'All']
];

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
        return '-';
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value));
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(`${value}T12:00:00`);
    return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const itemAmount = (item) => item?.spent_amount ?? item?.received_amount;

const itemLabel = (item) => {
    const transaction = item?.payee || item?.description_raw || 'Transaction';
    return `${formatDate(item?.transaction_date)} - ${transaction} - ${formatCurrency(itemAmount(item))}`;
};

const matchOptionLabel = (item) => {
    const transaction = item?.payee || item?.description_raw || 'Transaction';
    const parts = [
        formatDate(item?.transaction_date),
        transaction,
        formatCurrency(itemAmount(item)),
        item?.user_name || item?.cardholder_name || 'Unassigned',
    ];
    if (item?.card_last_four) {
        parts.push(`card ${item.card_last_four}`);
    }
    return parts.join(' — ');
};

export const ReceiptReview = ({ onSummaryChange = () => {} }) => {
    const [filter, setFilter] = useState('pending');
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [unmatchedFiles, setUnmatchedFiles] = useState([]);
    const [pendingCandidates, setPendingCandidates] = useState([]);
    const [summary, setSummary] = useState({ pending: 0, resolved: 0, unmatched: 0, deleted: 0, potential_duplicates: 0, unmapped: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, per_page: 100, total: 0, total_pages: 1 });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [lastSelectedItemId, setLastSelectedItemId] = useState(null);
    const [notice, setNotice] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [sending, setSending] = useState(false);
    const [showMappings, setShowMappings] = useState(false);
    const [mappings, setMappings] = useState([]);
    const [users, setUsers] = useState([]);
    const [loadingMappings, setLoadingMappings] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [mappingDrafts, setMappingDrafts] = useState({});
    const [savingMappingId, setSavingMappingId] = useState(null);
    const [recipientGroups, setRecipientGroups] = useState([]);
    const [recipientGroupDrafts, setRecipientGroupDrafts] = useState({});
    const [savingRecipientGroupId, setSavingRecipientGroupId] = useState(null);
    const [manualMatches, setManualMatches] = useState({});
    const [recipientAssignments, setRecipientAssignments] = useState({});
    const [savingRecipientAssignments, setSavingRecipientAssignments] = useState(false);
    const onSummaryChangeRef = useRef(onSummaryChange);

    useEffect(() => {
        onSummaryChangeRef.current = onSummaryChange;
    }, [onSummaryChange]);

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                filter,
                search,
                page: String(pagination.current_page),
                per_page: String(pagination.per_page),
                time: String(Date.now())
            });
            const response = await fetch(`${RECEIPT_API}?${params.toString()}`, { credentials: 'include' });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Could not load receipt review items.');
            }
            setItems(Array.isArray(payload.items) ? payload.items : []);
            setUnmatchedFiles(Array.isArray(payload.unmatched_files) ? payload.unmatched_files : []);
            setPendingCandidates(Array.isArray(payload.pending_candidates) ? payload.pending_candidates : []);
            setSummary(payload.summary || {});
            onSummaryChangeRef.current?.(payload.summary || {});
            setPagination((current) => ({
                ...current,
                ...(payload.pagination || {}),
                per_page: payload.pagination?.per_page || current.per_page
            }));
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not load receipt review items.' });
        } finally {
            setLoading(false);
        }
    }, [filter, pagination.current_page, pagination.per_page, search]);

    useEffect(() => {
        const delay = search ? 250 : 0;
        const timer = window.setTimeout(() => {
            loadItems();
        }, delay);
        return () => window.clearTimeout(timer);
    }, [loadItems, search]);

    useEffect(() => {
        setSelectedIds([]);
    }, [filter, pagination.current_page, search]);

    const loadMappings = useCallback(async () => {
        setLoadingMappings(true);
        try {
            const response = await fetch(`${RECEIPT_API}?action=mappings&time=${Date.now()}`, { credentials: 'include' });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Could not load cardholder mappings.');
            }
            const nextMappings = Array.isArray(payload.mappings) ? payload.mappings : [];
            setMappings(nextMappings);
            setMappingDrafts((current) => {
                const nextDrafts = { ...current };
                nextMappings.forEach((mapping) => {
                    const mappingId = String(mapping.id);
                    if (!nextDrafts[mappingId]) {
                        nextDrafts[mappingId] = {
                            user_id: mapping.user_id ? String(mapping.user_id) : '',
                            recipient_group_id: mapping.recipient_group_id ? String(mapping.recipient_group_id) : '',
                            receipt_request_exempt: Number(mapping.receipt_request_exempt) === 1,
                            apply_to_existing: true
                        };
                    }
                });
                return nextDrafts;
            });
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not load cardholder mappings.' });
        } finally {
            setLoadingMappings(false);
        }
    }, []);

    const loadRecipientGroups = useCallback(async () => {
        try {
            const response = await fetch(`${RECEIPT_API}?action=recipient_groups&time=${Date.now()}`, { credentials: 'include' });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Could not load receipt recipient groups.');
            }
            const nextGroups = Array.isArray(payload.groups) ? payload.groups : [];
            setRecipientGroups(nextGroups);
            setRecipientGroupDrafts((current) => {
                const nextDrafts = { ...current };
                nextGroups.forEach((group) => {
                    const groupId = String(group.id);
                    if (!nextDrafts[groupId]) {
                        nextDrafts[groupId] = { name: group.name || '', member_ids: (group.member_ids || []).map(String) };
                    }
                });
                return nextDrafts;
            });
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not load receipt recipient groups.' });
        }
    }, []);

    const loadUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const response = await fetch(`https://api.casedb.co/user.php?users=true&all=true&time=${Date.now()}`, { credentials: 'include' });
            const payload = await response.json().catch(() => ({}));
            if (Array.isArray(payload.users)) {
                setUsers(payload.users);
            }
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not load users for cardholder mappings.' });
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    useEffect(() => {
        if (showMappings) {
            loadMappings();
            loadUsers();
            loadRecipientGroups();
        }
    }, [loadMappings, loadRecipientGroups, loadUsers, showMappings]);

    const saveMapping = async (mapping, providedDraft = null) => {
        const mappingId = String(mapping.id);
        const draft = providedDraft || mappingDrafts[mappingId] || {
            user_id: mapping.user_id ? String(mapping.user_id) : '',
            recipient_group_id: mapping.recipient_group_id ? String(mapping.recipient_group_id) : '',
            receipt_request_exempt: Number(mapping.receipt_request_exempt) === 1,
            apply_to_existing: true
        };
        setSavingMappingId(mappingId);
        setNotice(null);
        try {
            const payload = await postJson({
                action: 'update_mapping',
                mapping_id: mapping.id,
                user_id: draft.user_id || '',
                recipient_group_id: draft.recipient_group_id || '',
                receipt_request_exempt: draft.receipt_request_exempt ? 1 : 0,
                apply_to_existing: draft.apply_to_existing ? 1 : 0
            });
            const backfilled = payload.backfilled_items || 0;
            const unmapped = payload.unmapped_items || 0;
            setNotice({
                type: 'success',
                text: unmapped > 0
                    ? `${payload.configured_name || 'Mapping'} updated and ${unmapped} pending item${unmapped === 1 ? '' : 's'} unmapped.`
                    : backfilled > 0
                    ? `${payload.configured_name || 'Mapping'} updated and ${backfilled} pending item${backfilled === 1 ? '' : 's'} backfilled.`
                    : `${payload.configured_name || 'Mapping'} updated.`
            });
            await loadMappings();
            await loadRecipientGroups();
            await loadItems();
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not update cardholder mapping.' });
        } finally {
            setSavingMappingId(null);
        }
    };

    const saveRecipientGroup = async (groupId, providedDraft = null) => {
        const draft = providedDraft || recipientGroupDrafts[groupId] || { name: '', member_ids: [] };
        setSavingRecipientGroupId(groupId);
        try {
            await postJson({
                action: 'save_recipient_group',
                group_id: groupId === 'new' ? 0 : Number(groupId),
                name: draft.name,
                member_ids: draft.member_ids.map(Number)
            });
            setNotice({ type: 'success', text: `Recipient group ${groupId === 'new' ? 'created' : 'updated'}.` });
            if (groupId === 'new') {
                setRecipientGroupDrafts((current) => ({ ...current, new: { name: '', member_ids: [] } }));
            }
            await loadRecipientGroups();
            return true;
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not save receipt recipient group.' });
            return false;
        } finally {
            setSavingRecipientGroupId(null);
        }
    };

    const postJson = async (body) => {
        await ensureCaseDbCsrfToken();
        const response = await fetch(RECEIPT_API, {
            method: 'POST',
            credentials: 'include',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            body: JSON.stringify(body)
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.success) {
            throw new Error(payload?.message || 'Receipt review request failed.');
        }
        return payload;
    };

    const handleUpload = async (event, action) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setUploading(action);
        setNotice(null);
        try {
            await ensureCaseDbCsrfToken();
            const data = new FormData();
            data.append('action', action);
            data.append('file', file);
            const response = await fetch(RECEIPT_API, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({}, 'POST'),
                body: data
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Import failed.');
            }
            const counts = payload.counts || {};
            const detail = action === 'import_transactions'
                ? `${counts.inserted || 0} added, ${counts.exact_duplicates || 0} exact duplicates skipped, ${counts.potential_duplicates || 0} potential duplicates flagged.`
                : `${counts.inserted || 0} card mappings saved, ${counts.unmatched_cards || 0} names need review.`;
            setNotice({ type: 'success', text: detail });
            await loadItems();
            if (action === 'import_cardholders' && showMappings) {
                await loadMappings();
            }
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Import failed.' });
        } finally {
            setUploading('');
        }
    };

    const handleToggleItem = (itemId) => {
        setSelectedIds((current) => {
            const next = current.includes(itemId)
                ? current.filter((id) => id !== itemId)
                : [...current, itemId];
            setLastSelectedItemId(next.includes(itemId) ? itemId : (next[next.length - 1] ?? null));
            return next;
        });
    };

    const handleToggleAll = () => {
        const visibleIds = items.map((item) => Number(item.id));
        setSelectedIds((current) => {
            const next = current.length === visibleIds.length ? [] : visibleIds;
            setLastSelectedItemId(next[next.length - 1] ?? null);
            return next;
        });
    };

    const handleStatusUpdate = async (state) => {
        if (selectedIds.length === 0) return;
        if (state === 'deleted' && !window.confirm(`Remove ${selectedIds.length} selected item${selectedIds.length === 1 ? '' : 's'} from the active receipt list?`)) {
            return;
        }
        try {
            const payload = await postJson({ action: 'update_items', item_ids: selectedIds, state });
            setNotice({ type: 'success', text: `${payload.updated || 0} item${payload.updated === 1 ? '' : 's'} updated.` });
            setSelectedIds([]);
            await loadItems();
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not update selected items.' });
        }
    };

    const assignSelectedRecipients = async (selectedUnmappedItems) => {
        const assignments = Object.fromEntries(selectedUnmappedItems
            .map((item) => [String(item.id), Number(recipientAssignments[String(item.id)] || 0)])
            .filter(([, userId]) => userId > 0));
        if (Object.keys(assignments).length !== selectedUnmappedItems.length) {
            setNotice({ type: 'error', text: 'Select a recipient for every selected unmapped item.' });
            return;
        }
        setSavingRecipientAssignments(true);
        try {
            const payload = await postJson({
                action: 'assign_recipients',
                item_ids: selectedUnmappedItems.map((item) => Number(item.id)),
                recipient_assignments: assignments
            });
            setNotice({ type: 'success', text: `${payload.updated || 0} receipt item${payload.updated === 1 ? '' : 's'} assigned to the selected recipient${payload.updated === 1 ? '' : 's'}.` });
            await loadItems();
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not assign receipt recipients.' });
        } finally {
            setSavingRecipientAssignments(false);
        }
    };

    const previewNotifications = async (scope, explicitItemIds = null) => {
        const itemIds = scope === 'all_pending' ? [] : (explicitItemIds || selectedIds);
        try {
            const payload = await postJson({
                action: 'preview_notifications',
                scope,
                item_ids: itemIds
            });
            setConfirmation(payload);
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not prepare receipt notifications.' });
        }
    };

    const sendNotifications = async () => {
        if (!confirmation?.confirmation_token) return;
        setSending(true);
        try {
            const payload = await postJson({ action: 'send_notifications', confirmation_token: confirmation.confirmation_token });
            const sentCount = Array.isArray(payload.sent) ? payload.sent.length : 0;
            const errorCount = Array.isArray(payload.errors) ? payload.errors.length : 0;
            setNotice({
                type: errorCount > 0 ? 'error' : 'success',
                text: errorCount > 0
                    ? `${sentCount} email group${sentCount === 1 ? '' : 's'} sent; ${errorCount} could not be sent.`
                    : `${sentCount} user-scoped receipt email${sentCount === 1 ? '' : 's'} sent.`
            });
            setConfirmation(null);
            setSelectedIds([]);
            await loadItems();
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not send receipt notifications.' });
        } finally {
            setSending(false);
        }
    };

    const handleMatchInbound = async (fileId) => {
        const itemId = Number(manualMatches[fileId] || 0);
        if (!itemId) {
            setNotice({ type: 'error', text: 'Choose a pending receipt item before matching this PDF.' });
            return;
        }
        try {
            await postJson({ action: 'match_inbound_file', file_id: fileId, item_id: itemId });
            setNotice({ type: 'success', text: 'Receipt PDF matched and the transaction was resolved.' });
            await loadItems();
        } catch (error) {
            setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not match receipt PDF.' });
        }
    };

    const changeFilter = (nextFilter) => {
        setFilter(nextFilter);
        setPagination((current) => ({ ...current, current_page: 1 }));
    };

    const changePage = (nextPage) => {
        setPagination((current) => ({
            ...current,
            current_page: Math.max(1, Math.min(current.total_pages || 1, nextPage))
        }));
    };

    const selectedVisibleCount = selectedIds.filter((id) => items.some((item) => Number(item.id) === Number(id))).length;
    const selectedUnmappedItems = items.filter((item) => selectedIds.includes(Number(item.id)) && !item.user_id && !item.mapped_cardholder_id && !item.resolved_at && !item.deleted_at);
    const recipientOptions = users
        .filter((user) => user?.id && user?.access_level !== 'no access')
        .map((user) => ({ value: String(user.id), label: user.email ? `${user.name} (${user.email})` : user.name || `User ${user.id}` }));

    useEffect(() => {
        if (selectedUnmappedItems.length > 0 && users.length === 0 && !loadingUsers) {
            loadUsers();
        }
    }, [loadUsers, loadingUsers, selectedUnmappedItems.length, users.length]);

    useEffect(() => {
        const selectedItemIds = new Set(selectedUnmappedItems.map((item) => String(item.id)));
        setRecipientAssignments((current) => Object.fromEntries(Object.entries(current)
            .filter(([itemId]) => selectedItemIds.has(itemId))));
    }, [selectedIds, items]);

    return (
        <div className='receipt-review accounting-section'>
            <div className='accounting-header receipt-review-header'>
                <div className='accounting-header-left'>
                    <h3>Receipt Review {selectedIds.length > 0 && `(${selectedIds.length} selected)`}</h3>
                    <div className='filter-buttons receipt-review-filters'>
                        {filterOptions.map(([value, label]) => {
                            const count = value === 'pending'
                                ? summary.pending
                                : value === 'resolved'
                                    ? summary.resolved
                                    : value === 'unmatched'
                                        ? summary.unmatched
                                        : value === 'deleted'
                                            ? summary.deleted
                                            : null;
                            return (
                                <button
                                    type='button'
                                    key={value}
                                    className={`filter-button ${filter === value ? 'active' : ''}`}
                                    onClick={() => changeFilter(value)}
                                >
                                    {label}{count !== null ? ` (${count || 0})` : ''}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className='accounting-header-right receipt-review-actions'>
                    <label className='action alt receipt-review-upload' title='Upload QuickBooks For Review CSV'>
                        {uploading === 'import_transactions' ? <Loader2 size={16} className='spin' /> : <Upload size={16} />}
                        QuickBooks CSV
                        <input type='file' accept='.csv,text/csv' onChange={(event) => handleUpload(event, 'import_transactions')} disabled={Boolean(uploading)} />
                    </label>
                    {/*<label className='action alt receipt-review-upload' title='Upload cardholder mapping CSV'>
                        {uploading === 'import_cardholders' ? <Loader2 size={16} className='spin' /> : <Users size={16} />}
                        Card Map
                        <input type='file' accept='.csv,text/csv' onChange={(event) => handleUpload(event, 'import_cardholders')} disabled={Boolean(uploading)} />
                    </label>*/}
                    <button className='action alt' onClick={() => setShowMappings(true)} title='View cardholder mappings'>
                        <Users size={16} />
                        Mappings
                    </button>
                    <button className='action alt' onClick={loadItems} disabled={loading} title='Refresh receipt review'>
                        <RefreshCw size={16} className={loading ? 'spin' : ''} />
                    </button>
                </div>
            </div>

            {notice && (
                <div className={`receipt-review-notice ${notice.type}`}>
                    {notice.type === 'error' ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
                    <span>{notice.text}</span>
                    <button type='button' className='receipt-review-notice-dismiss' onClick={() => setNotice(null)} title='Dismiss notification'>
                        <XCircle size={15} />
                    </button>
                </div>
            )}

            <div className='receipt-review-toolbar'>
                <label className='receipt-review-search'>
                    <Search size={16} />
                    <input value={search} onChange={(event) => {
                        setSearch(event.target.value);
                        setPagination((current) => ({ ...current, current_page: 1 }));
                    }} placeholder='Search merchant, cardholder, category, or card' />
                </label>
                {filter !== 'unmatched' && (
                    <div className='receipt-review-bulk-actions'>
                        <button type='button' className='action alt' onClick={() => previewNotifications('all_pending')} disabled={loading || summary.pending === 0}>
                            <BellRing size={16} />
                            Notify All Pending
                        </button>
                        <button type='button' className='action alt' onClick={() => previewNotifications('selected')} disabled={loading || selectedIds.length === 0}>
                            <Send size={16} />
                            Send Selected
                        </button>
                        <button type='button' className='action alt' onClick={() => handleStatusUpdate('resolved')} disabled={loading || selectedIds.length === 0}>
                            <CheckCircle2 size={16} />
                            Resolve
                        </button>
                        <button type='button' className='action destroy' onClick={() => handleStatusUpdate('deleted')} disabled={loading || selectedIds.length === 0}>
                            <Trash2 size={16} />
                        </button>
                    </div>
                )}
                <div className='select-wrapper receipt-review-page-size'>
                    <select className='filter-select' value={pagination.per_page} onChange={(event) => setPagination((current) => ({ ...current, per_page: Number(event.target.value), current_page: 1 }))}>
                        {[25, 50, 100, 200].map((size) => <option key={size} value={size}>{size} per page</option>)}
                    </select>
                    <ChevronDown size={16} className='select-chevron' />
                </div>
            </div>

            {selectedUnmappedItems.length > 0 && (
                <section className='receipt-review-recipient-assignments' aria-label='Assign recipients to selected receipt items'>
                    <div className='receipt-review-recipient-assignments-heading'>
                        <div><Users size={16} /><strong>Assign recipients</strong></div>
                        <span>{selectedUnmappedItems.length} selected unmapped item{selectedUnmappedItems.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className='receipt-review-recipient-assignment-list'>
                        {selectedUnmappedItems.map((item) => (
                            <div className='receipt-review-recipient-assignment' key={item.id}>
                                <span title={itemLabel(item)}>{itemLabel(item)}</span>
                                <SearchSelect
                                    value={recipientAssignments[String(item.id)] || null}
                                    onChange={(userId) => setRecipientAssignments((current) => ({ ...current, [String(item.id)]: userId || '' }))}
                                    options={recipientOptions}
                                    placeholder={loadingUsers ? 'Loading users...' : 'Search recipient'}
                                    disabled={loadingUsers || savingRecipientAssignments}
                                    minMatchScore={0.55}
                                    portal
                                />
                            </div>
                        ))}
                    </div>
                    <div className='receipt-review-recipient-assignment-actions'>
                        <button
                            type='button'
                            className='action alt'
                            onClick={() => assignSelectedRecipients(selectedUnmappedItems)}
                            disabled={loadingUsers || savingRecipientAssignments || selectedUnmappedItems.some((item) => !recipientAssignments[String(item.id)])}
                        >
                            {savingRecipientAssignments ? <Loader2 size={16} className='spin' /> : <Users size={16} />}
                            Assign Recipients
                        </button>
                    </div>
                </section>
            )}

            {filter === 'unmatched' ? (
                <div className='invoice-table-container receipt-review-table-container'>
                    <table className='invoice-table receipt-review-table subtext'>
                        <thead>
                            <tr>
                                <th className='receipt-review-file'>PDF</th>
                                <th className='invoice-small'>Sent By</th>
                                <th className='invoice-small'>Suggested Date</th>
                                <th className='invoice-small'>Suggested Amount</th>
                                <th className='receipt-review-match'>Match Pending Item</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan='5' className='loading-row'><Loader2 size={16} className='spin' /> Loading unmatched receipt PDFs...</td></tr>
                            ) : unmatchedFiles.length === 0 ? (
                                <tr><td colSpan='5' className='empty-row'><CheckCircle2 size={18} /> No unmatched receipt PDFs.</td></tr>
                            ) : unmatchedFiles.map((file) => (
                                <tr key={file.id} className='invoice-row receipt-review-row'>
                                    <td className='receipt-review-file' title={file.filename || ''}>
                                        {file.jane_file_id ? (
                                            <button type='button' className='receipt-review-file-link' onClick={() => window.open(`/file/${file.jane_file_id}`, '_blank', 'noopener')}>
                                                <FileUp size={16} /> {file.filename || `Receipt PDF ${file.id}`}
                                            </button>
                                        ) : <><FileUp size={16} /> {file.filename || `Receipt PDF ${file.id}`}</>}
                                    </td>
                                    <td className='invoice-small'>{file.sender_name || file.sender_email || 'Unknown sender'}</td>
                                    <td className='invoice-small'>{formatDate(file.suggested_date)}</td>
                                    <td className='invoice-small'>{formatCurrency(file.suggested_amount)}</td>
                                    <td className='receipt-review-match'>
                                        <div className='receipt-review-match-control'>
                                            <SearchSelect
                                                value={manualMatches[file.id] || null}
                                                onChange={(itemId) => setManualMatches((current) => ({ ...current, [file.id]: itemId || '' }))}
                                                options={pendingCandidates.map((item) => ({ value: String(item.id), label: matchOptionLabel(item) }))}
                                                placeholder='Search pending charges'
                                                minMatchScore={0.4}
                                                portal
                                            />
                                            <button type='button' className='action alt' onClick={() => handleMatchInbound(Number(file.id))} disabled={!manualMatches[file.id]} title='Match receipt PDF'>
                                                <Link2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className='invoice-table-container receipt-review-table-container'>
                    <table className='invoice-table receipt-review-table subtext'>
                        <thead>
                            <tr>
                                <th className='invoice-select'>
                                    <Checkbox value={items.length > 0 && selectedVisibleCount === items.length} onChange={handleToggleAll} />
                                </th>
                                <th className='invoice-small'>Date</th>
                                <th className='receipt-review-transaction'>Transaction</th>
                                <th className='invoice-small'>Category</th>
                                <th className='invoice-small'>Amount</th>
                                <th className='invoice-small'>Card</th>
                                <th className='invoice-small'>Cardholder</th>
                                <th className='receipt-review-status'>{filter === 'resolved' ? 'Receipt' : 'Status'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan='8' className='loading-row'><Loader2 size={16} className='spin' /> Loading receipt review items...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan='8' className='empty-row'><FileQuestion size={18} /> No {filter === 'all' ? '' : filter} receipt review items found.</td></tr>
                            ) : (() => {
                                const visibleSelected = items.filter((item) => selectedIds.includes(Number(item.id)));
                                const actionsAnchorId = visibleSelected.some((item) => Number(item.id) === Number(lastSelectedItemId))
                                    ? Number(lastSelectedItemId)
                                    : Number(visibleSelected[visibleSelected.length - 1]?.id || 0);
                                const selectedTotal = visibleSelected.reduce((sum, item) => sum + (Number(itemAmount(item)) || 0), 0);
                                const rows = [];
                                items.forEach((item) => {
                                    const isSelected = selectedIds.includes(Number(item.id));
                                    const isPotentialDuplicate = Boolean(item.potential_duplicate_of_item_id);
                                    const isResolved = Boolean(item.resolved_at);
                                    const isDeleted = Boolean(item.deleted_at);
                                    const isUnmapped = !item.user_id && !item.mapped_cardholder_id;
                                    rows.push(
                                        <tr key={item.id} className={`invoice-row receipt-review-row ${isSelected ? 'selected-row' : ''}`} onClick={() => handleToggleItem(Number(item.id))}>
                                            <td className='invoice-select' onClick={(event) => event.stopPropagation()}><Checkbox value={isSelected} onChange={() => handleToggleItem(Number(item.id))} /></td>
                                            <td className='invoice-small'>{formatDate(item.transaction_date)}</td>
                                            <td className='receipt-review-transaction' title={item.description_raw}>
                                                <strong>{item.payee || item.description_raw}</strong>
                                                {item.payee && item.description_raw && item.description_raw !== item.payee && <span>{item.description_raw}</span>}
                                            </td>
                                            <td className='invoice-small receipt-review-wrap'>{item.category_label || '-'}</td>
                                            <td className='invoice-small'>{formatCurrency(itemAmount(item))}</td>
                                            <td className='invoice-small'>{item.card_last_four ? `•••• ${item.card_last_four}` : '-'}</td>
                                            <td className='invoice-small receipt-review-wrap'>{item.user_name || item.cardholder_name || 'Unmapped'}</td>
                                            <td className='receipt-review-status' onClick={(event) => event.stopPropagation()}>
                                                {isDeleted ? <span className='receipt-status deleted'>Deleted</span>
                                                    : isResolved ? (
                                                        filter === 'resolved' ? (
                                                            (item.receipt_files || []).some((file) => file.jane_file_id) ? (
                                                                <span className='receipt-review-resolved-status'>
                                                                    {(item.receipt_files || []).filter((file) => file.jane_file_id).map((file) => (
                                                                        <button
                                                                            type='button'
                                                                            key={file.id}
                                                                            className='receipt-review-file-link'
                                                                            onClick={() => window.open(`/file/${file.jane_file_id}`, '_blank', 'noopener')}
                                                                            title={`Open ${file.filename || 'receipt PDF'}${file.sender_email ? ` from ${file.sender_email}` : ''}`}
                                                                        >
                                                                            <FileUp size={14} /> {file.filename || 'Receipt PDF'}
                                                                        </button>
                                                                    ))}
                                                                </span>
                                                            ) : <span className='subtext'>{item.resolution_source === 'inbound_auto' ? 'Emailed receipt' : 'Marked resolved manually'}</span>
                                                        ) : (
                                                            <span className='receipt-review-resolved-status'>
                                                                <span className='receipt-status resolved'>Resolved</span>
                                                                {(item.receipt_files || []).filter((file) => file.jane_file_id).map((file) => (
                                                                    <button
                                                                        type='button'
                                                                        key={file.id}
                                                                        className='receipt-review-file-link'
                                                                        onClick={() => window.open(`/file/${file.jane_file_id}`, '_blank', 'noopener')}
                                                                        title={`Open ${file.filename || 'receipt PDF'}${file.sender_email ? ` from ${file.sender_email}` : ''}`}
                                                                    >
                                                                        <FileUp size={14} /> {file.filename || 'Receipt PDF'}
                                                                    </button>
                                                                ))}
                                                            </span>
                                                        )
                                                    )
                                                        : isUnmapped ? <span className='receipt-status unmapped'>Unmapped</span>
                                                            : isPotentialDuplicate ? <span className='receipt-status duplicate' title={`Potential duplicate of item ${item.potential_duplicate_id}`}>Potential duplicate</span>
                                                                : item.first_requested_at ? <span className='receipt-status requested'>Requested</span>
                                                                    : <span className='receipt-status pending'>Pending</span>}
                                            </td>
                                        </tr>
                                    );
                                    if (selectedIds.length > 0 && Number(item.id) === actionsAnchorId) {
                                        const anchorSendable = !isDeleted && !isResolved;
                                        rows.push(
                                            <tr key={`actions-${item.id}`} className='receipt-review-actions-row' onClick={(event) => event.stopPropagation()}>
                                                <td colSpan='8' className='receipt-review-actions-cell'>
                                                    <div className='receipt-review-actions-inline'>
                                                        <div className='receipt-review-actions-summary small-text'>
                                                            <strong>{selectedIds.length} selected</strong>
                                                            <span>{formatCurrency(selectedTotal)}</span>
                                                        </div>
                                                        <div className='receipt-review-actions-buttons'>
                                                            <button type='button' className='action' onClick={() => previewNotifications('selected', [Number(item.id)])} disabled={loading || !anchorSendable} title={anchorSendable ? `Send a receipt request for ${itemLabel(item)}` : 'This item is resolved or removed.'}>
                                                                <Send size={15} /> Send This Item
                                                            </button>
                                                            <button type='button' className='action alt' onClick={() => previewNotifications('selected')} disabled={loading || selectedIds.length === 0}>
                                                                <BellRing size={15} /> Send {selectedIds.length} Selected
                                                            </button>
                                                            <button type='button' className='action alt' onClick={() => handleStatusUpdate('resolved')} disabled={loading}>
                                                                <CheckCircle2 size={15} /> Mark Resolved
                                                            </button>
                                                            <button type='button' className='action destroy receipt-review-delete' onClick={() => handleStatusUpdate('deleted')} disabled={loading}>
                                                                <Trash2 size={15} /> Remove
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                });
                                return rows;
                            })()}
                        </tbody>
                    </table>
                </div>
            )}

            <div className='pagination-controls receipt-review-pagination'>
                <button type='button' className='action alt' disabled={loading || pagination.current_page <= 1} onClick={() => changePage(pagination.current_page - 1)}>Previous</button>
                <span className='pagination-info subtext'>Page {pagination.current_page} of {pagination.total_pages || 1} ({pagination.total || 0} items)</span>
                <button type='button' className='action alt' disabled={loading || pagination.current_page >= (pagination.total_pages || 1)} onClick={() => changePage(pagination.current_page + 1)}>Next</button>
            </div>

            {confirmation && (
                <Modal
                    
                    title='Confirm Receipt Notifications'
                    instructions='One email per cardholder. Recipient groups get a single email addressed to every member with the full list of group items.'
                    onClose={() => !sending && setConfirmation(null)}
                    footer={<div className='modal-footer-actions'><button type='button' className='action alt' disabled={sending} onClick={() => setConfirmation(null)}>Cancel</button><button type='button' className='action' disabled={sending} onClick={sendNotifications}>{sending ? <Loader2 size={16} className='spin' /> : <Send size={16} />} Send {confirmation.groups?.length || 0} Email{confirmation.groups?.length === 1 ? '' : 's'}</button></div>}
                >
                    <div className='modal-content-wrapper'>
                        <div className='receipt-review-confirmation'>
                            {confirmation.groups?.map((group) => (
                                <div className='receipt-review-confirmation-group' key={group.recipient_group_id ? `group-${group.recipient_group_id}` : `user-${group.user_id}`}>
                                    <div className='receipt-review-confirmation-group-header small-text'>
                                        <strong>{group.recipient_group_name || group.name || group.email}{group.recipient_group_id ? ` (${(group.emails || []).length} member${(group.emails || []).length === 1 ? '' : 's'})` : ''}</strong>
                                        <span>{group.email}</span>
                                    </div>
                                    <span className='receipt-review-confirmation-group-item-count small-text'>{group.item_count} item{group.item_count === 1 ? '' : 's'}</span>
                                </div>
                            ))}
                            {confirmation.skipped?.length > 0 && <div className='receipt-review-skipped'>{confirmation.skipped.length} selected item{confirmation.skipped.length === 1 ? '' : 's'} will not be emailed because the user is unmapped, exempt, or has no valid email.</div>}
                        </div>
                    </div>
                </Modal>
            )}

            {showMappings && (
                <CardholderMappingsModal
                    mappings={mappings}
                    users={users}
                    recipientGroups={recipientGroups}
                    loading={loadingMappings || loadingUsers}
                    uploading={Boolean(uploading)}
                    savingMappingId={savingMappingId}
                    savingRecipientGroupId={savingRecipientGroupId}
                    onSaveMapping={saveMapping}
                    onSaveRecipientGroup={saveRecipientGroup}
                    onUpload={(event) => handleUpload(event, 'import_cardholders')}
                    onClose={() => setShowMappings(false)}
                />
            )}
        </div>
    );
};

export default ReceiptReview;
